import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

interface SalesEntry {
    date: string;
    total_sales: string;
}

interface PurchaseEntry {
    date: string;
    total_purchases: string;
}

const Charts: React.FC = () => {
    const [weeklyData, setWeeklyData] = useState<{ date: string; total_sales: number; total_purchases: number }[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const salesResponse = await axios.get('https://api.sominnovations.xyz/Branches/num_Sales/?period=weekly');
                const allSales = salesResponse.data.sales || [];
                const groupedSales: { [key: string]: number } = allSales.reduce((acc: any, sale: any) => {
                    const saleDate = new Date(sale.date_sold).toLocaleDateString('en-GB');
                    const totalAmount = parseFloat(sale.total_amount) || 0;
                    acc[saleDate] = acc[saleDate] ? acc[saleDate] + totalAmount : totalAmount;
                    return acc;
                }, {});

                const weeklySales: SalesEntry[] = Object.keys(groupedSales).map((date) => ({
                    date,
                    total_sales: `Ksh ${groupedSales[date].toFixed(2)}`,
                }));

                const purchasesResponse = await axios.get('https://api.sominnovations.xyz/Branches/purchases_by_month/', {
                    params: { period: 'weekly' },
                });
                const weeklyPurchases: PurchaseEntry[] = purchasesResponse.data.daily_purchases;

                const combinedData = weeklyPurchases.map((purchaseEntry) => {
                    const salesEntry = weeklySales.find((s) => s.date === purchaseEntry.date) || { total_sales: 'Ksh 0.00' };

                    const parseCurrency = (val: string | number) => {
                        if (typeof val === 'number') return val;
                        if (typeof val === 'string') {
                            return parseFloat(val.replace(/[^0-9.-]+/g, '')) || 0;
                        }
                        return 0;
                    };

                    return {
                        date: purchaseEntry.date,
                        total_sales: parseCurrency(salesEntry.total_sales),
                        total_purchases: parseCurrency(purchaseEntry.total_purchases),
                    };
                });

                setWeeklyData(combinedData);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    return (
        <div className="">
            <div className="row">
                <div className="col-12">
                    <div className="card" style={{ borderRadius: '12px', backgroundColor: '#fff' }}>
                        <div className="card-body">
                            <h5
                                className="card-title text-center mb-4"
                                style={{ fontWeight: 600, color: '#343a40', fontSize: '1.5rem' }}
                            >
                                Weekly Sales & Purchases
                            </h5>
                            <div style={{ width: '100%', height: '500px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={weeklyData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        <XAxis dataKey="date" stroke="#495057" tick={{ fontSize: 12 }} />
                                        <YAxis stroke="#495057" tick={{ fontSize: 12 }} />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                backgroundColor: '#ffffff',
                                            }}
                                            labelStyle={{ color: '#495057' }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                paddingTop: '10px',
                                                fontSize: '14px',
                                                color: '#495057',
                                            }}
                                        />
                                        <Bar dataKey="total_sales" name="Sales" fill="#5271ff" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="total_purchases" name="Purchases" fill="#6c5ce7" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Charts;
